import { useDispatch } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { Error } from 'components/common/error/Error';
import { setFormField } from 'state/retrieve-quote/retrieve-quote-slice';
import { retrieveQuoteThunk } from 'state/retrieve-quote/retrieve-quote-thunks';
import { useRetrieveQuote } from 'components/retrieve-quote/hooks/useRetrieveQuote';
import { usePopulateEmailQuoteJourney } from 'components/retrieve-quote/hooks/usePopulateEmailQuoteJourney';
import { Feature } from 'components/common/feature/Feature';
import { initiateThunk } from 'state/initiate/initiate-thunks';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsPostcodeInput } from 'components/common/ns-postcode-input/NsPostcodeInput';
import { NsEmailInput } from 'components/common/ns-email-input/NsEmailInput';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsStopgap } from 'components/common/ns-stopgap/NsStopgap';
import { useNavigation } from 'hooks/useNavigation';
import { ToggleJourney } from 'components/app/routes/ToggleJourney';

export const testId = 'retrieveQuoteTestId';

export const RetrieveQuote = () => {
  const { allowJourneyDefault } = useFlags();
  const dispatch = useDispatch();
  const {
    quoteId,
    postcode,
    email,
    isLoading,
    apiErrors,
    mask
  } = useRetrieveQuote();
  const populateEmailQuoteJourney = usePopulateEmailQuoteJourney();
  const { next, eventTypes } = useNavigation();

  const handleChange = (event) => {
    const {
      target: { value, name }
    } = event;
    dispatch(setFormField({ value, name }));
  };

  const handleQuoteExpired = (e) => {
    if (!Array.isArray(e)) {
      return;
    }
    const quoteExpiredError = e.some(
      ({ code }) => code === 'FetchCustomerQuoteException'
    );
    if (quoteExpiredError) {
      next({ eventType: eventTypes.QUOTE_EXPIRED });
    }
  };

  const handleGoNext = async () => {
    try {
      await dispatch(initiateThunk());
      const quoteData = await dispatch(
        retrieveQuoteThunk({
          quoteId,
          postcode,
          email
        })
      );
      await populateEmailQuoteJourney(quoteData);
      next({ eventType: eventTypes.NEXT });
    } catch (error) {
      handleQuoteExpired(error);
      error && console.error(error);
    }
  };

  return (
    <Feature
      feature={allowJourneyDefault}
      inactiveComponent={<ToggleJourney off />}
    >
      {isLoading ? (
        <NsStopgap />
      ) : (
        <NsPanel>
          <NsContent>
            <NsTitle title="Get your quote" />
            <p>Please enter your details to access your saved information.</p>
          </NsContent>
          <NsForm testId={testId} onSubmit={handleGoNext}>
            <NsPostcodeInput
              id="postcode"
              name="postcode"
              label="Your postcode"
              value={postcode}
              onChange={handleChange}
              inputAttributes={mask}
            />
            <NsEmailInput
              id="email"
              name="email"
              type="email"
              label="Email address"
              placeholder="Email address"
              value={email}
              onChange={handleChange}
              inputAttributes={mask}
            />
            <Error errors={apiErrors} />
            <NsPrimaryCta isLoading={isLoading}>Next</NsPrimaryCta>
          </NsForm>
        </NsPanel>
      )}
    </Feature>
  );
};

export default RetrieveQuote;
